import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { useWorkspaceDuplexer } from './use-workspace-duplexer';
import { queryClient, queryKeys } from './query-client';
import type { WebsitePresence } from './presence-checklist';
import type { WorkspaceWebsite } from './workspace-website';

// Client data layer for the sidebar's Workspaces section. One fetch to /api/workspaces (the symphony-bff
// ListWorkspaces aggregation) returns the caller's top-level workspaces + each one's child ("project")
// count. orion-web's CLIENT bundle must not import common-lib, so the shape lives here as a client-local
// contract matching what the route returns.


/** A top-level workspace for the sidebar — its identity + child ("project") count. */
export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  icon?: string;
  /** The workspace's one-line descriptor (e.g. "Restaurant • Milan") — the new-workspace brief card's subtitle. */
  description?: string;
  projects: number;
  /** The Space's primary Wix site (symphony-bff `WorkspaceWebsite`), when it has one — the Website tile. */
  website?: WorkspaceWebsite;
  /**
   * The Space's presence (symphony-bff `WorkspaceSummary.presence`) — the header strip's source (`headerPresence`);
   * it is the only carrier for a non-Wix site and for a Space with no website. Absent from an older server.
   */
  presence?: WebsitePresence;
}

export type WorkspacesState =
  | { status: 'loading' }
  | { status: 'ready'; workspaces: WorkspaceSummary[] }
  | { status: 'error' };

/**
 * Last-known non-empty icon per workspace id — the same source of truth native keeps
 * (`workspaceIconById` + `mergeWorkspaceIcons` in `SymphonyStore`).
 *
 * ListWorkspaces can come back with a workspace's `icon` absent on a transient read, and replacing the
 * list wholesale would flip that badge to its initials tile and back — the bug iOS's `WorkspaceBadge`
 * comment records as "the badge never gets stuck on the fallback while holding a valid URL".
 *
 * The rule, 1:1 with native: a FETCH may only ever ADD to this map, never clear an entry; an explicit
 * user EDIT (`updateWorkspaceIcon`) is authoritative in both directions, so removing a photo really
 * removes it instead of being reinstated by the next read.
 */
const knownIcons = new Map<string, string>();

/** Record every non-empty icon in `list`. A merge — never clears a previously-known entry. */
function mergeWorkspaceIcons(list: WorkspaceSummary[]): void {
  for (const w of list) {
    if (w.id && w.icon) knownIcons.set(w.id, w.icon);
  }
}

/** Fill in an icon the response omitted from what we already knew. */
function applyKnownIcons(list: WorkspaceSummary[]): WorkspaceSummary[] {
  return list.map((w) => (w.icon || !w.id ? w : { ...w, icon: knownIcons.get(w.id) }));
}

export async function fetchWorkspaces(): Promise<WorkspaceSummary[]> {
  const res = await apiFetch(`${API_BASE}/api/workspaces`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { workspaces?: WorkspaceSummary[] };
  const workspaces = data.workspaces ?? [];
  mergeWorkspaceIcons(workspaces);
  return applyKnownIcons(workspaces);
}

/**
 * Rename a workspace (name only — the slug/URL is untouched). Optimistic: the new name is patched into
 * the shared React Query cache immediately so the sidebar + every open pane update at once, then persisted
 * via /api/workspace-rename (WorkspaceService UpdateWorkspace). If the server rejects, the cache is rolled
 * back to its prior state and the call rejects so the caller can surface the failure.
 */
export async function renameWorkspace(id: string, name: string): Promise<void> {
  const prev = queryClient.getQueryData<WorkspaceSummary[]>(queryKeys.workspaces);
  if (prev) {
    queryClient.setQueryData<WorkspaceSummary[]>(
      queryKeys.workspaces,
      prev.map((w) => (w.id === id ? { ...w, name } : w)),
    );
  }
  try {
    const res = await apiFetch(`${API_BASE}/api/workspace-rename`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ workspaceId: id, name }),
    });
    if (!res.ok) {
      // Surface the real cause: in `next dev` the /api/* calls are proxied to the DEPLOYED prod handler
      // (see dev-proxy.mjs), so a brand-new route 404s until it ships; a deployed run would show the
      // backend's actual error here instead.
      const detail = await res.text().catch(() => '');
      console.error(`[renameWorkspace] failed: HTTP ${res.status}`, detail.slice(0, 500));
      throw new Error(`HTTP ${res.status}`);
    }
    // Reconcile with server truth so the displayed name is authoritative (not just our optimistic guess).
    try {
      queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, await fetchWorkspaces());
    } catch {
      /* keep the optimistic name if the confirming refetch hiccups */
    }
  } catch (err) {
    if (prev) queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, prev); // roll back
    throw err;
  }
}

/**
 * Persist the order the user just dragged their spaces into — `orderedIds` is the whole list, in order.
 * Optimistic in the same way as `renameWorkspace`: the shared React Query cache is reordered first so the
 * sidebar and the home strip move together, then it is persisted via /api/workspace-reorder (one
 * revision-guarded UpdateWorkspace per moved workspace, server-side).
 *
 * Rolls the cache back and rejects if the server refuses OR if it only got part of the order in
 * (`failedIds`) — a partly-applied order is not the one the user asked for, so showing it would be a lie.
 * Mirrors native's `SymphonyStore.reorderWorkspaces`.
 */
export async function reorderWorkspaces(orderedIds: string[]): Promise<void> {
  const prev = queryClient.getQueryData<WorkspaceSummary[]>(queryKeys.workspaces);
  if (prev) {
    const byId = new Map(prev.map((w) => [w.id, w]));
    // The requested order first, then anything the caller didn't name (a workspace Orion created while
    // the dialog was open) — exactly the server's rule, so the optimistic list matches what lands.
    const reordered = [
      ...orderedIds.map((id) => byId.get(id)).filter((w): w is WorkspaceSummary => Boolean(w)),
      ...prev.filter((w) => !orderedIds.includes(w.id)),
    ];
    queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, reordered);
  }
  try {
    const res = await apiFetch(`${API_BASE}/api/workspace-reorder`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ orderedIds }),
    });
    if (!res.ok) {
      // Same caveat as renameWorkspace: in `next dev` the /api/* calls are proxied to the DEPLOYED prod
      // handler, so this route 404s locally until it ships.
      const detail = await res.text().catch(() => '');
      console.error(`[reorderWorkspaces] failed: HTTP ${res.status}`, detail.slice(0, 500));
      throw new Error(`HTTP ${res.status}`);
    }
    const body = (await res.json().catch(() => ({}))) as { failedIds?: string[] };
    if (body.failedIds?.length) {
      console.error('[reorderWorkspaces] partially applied', body.failedIds);
      throw new Error('partially_applied');
    }
  } catch (err) {
    if (prev) queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, prev); // roll back
    // Re-read so the list shows what the server actually holds, not our rolled-back guess — a partial
    // write means neither the old nor the requested order is right.
    try {
      queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, await fetchWorkspaces());
    } catch {
      /* the rollback above stands */
    }
    throw err;
  }
}

/**
 * Set or clear a workspace's image. Pass a minted WixMP url to set it, or '' to CLEAR it (back to the
 * initials tile) — an empty string is a valid update here, which is why this isn't folded into
 * `renameWorkspace`. Optimistic in exactly the same way: the icon is patched into the shared React
 * Query cache first so the badge, the sidebar and every open pane change at once, then persisted via
 * /api/workspace-icon (WorkspaceService UpdateWorkspace). Rolls back and rejects if the server refuses.
 * Mirrors native's `SymphonyStore.updateWorkspaceImage`.
 */
export async function updateWorkspaceIcon(id: string, iconUrl: string): Promise<void> {
  const prev = queryClient.getQueryData<WorkspaceSummary[]>(queryKeys.workspaces);
  // An explicit edit is authoritative over the merge cache in BOTH directions — otherwise "Remove
  // photo" would be silently reinstated by the next read. Mirrors native's `updateWorkspaceImage`.
  const previousKnownIcon = knownIcons.get(id);
  if (iconUrl) knownIcons.set(id, iconUrl);
  else knownIcons.delete(id);
  if (prev) {
    queryClient.setQueryData<WorkspaceSummary[]>(
      queryKeys.workspaces,
      // '' means "no icon" to the badge, which reads `icon` as optional — store undefined, not ''.
      prev.map((w) => (w.id === id ? { ...w, icon: iconUrl || undefined } : w)),
    );
  }
  try {
    const res = await apiFetch(`${API_BASE}/api/workspace-icon`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ workspaceId: id, icon: iconUrl }),
    });
    if (!res.ok) {
      // Same caveat as renameWorkspace: in `next dev` the /api/* calls are proxied to the DEPLOYED
      // prod handler, so this route 404s locally until it ships.
      const detail = await res.text().catch(() => '');
      console.error(`[updateWorkspaceIcon] failed: HTTP ${res.status}`, detail.slice(0, 500));
      throw new Error(`HTTP ${res.status}`);
    }
    try {
      queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, await fetchWorkspaces());
    } catch {
      /* keep the optimistic icon if the confirming refetch hiccups */
    }
  } catch (err) {
    if (prev) queryClient.setQueryData<WorkspaceSummary[]>(queryKeys.workspaces, prev); // roll back
    // Roll the merge cache back with it, or a failed edit would leave the badge holding a URL the
    // server rejected.
    if (previousKnownIcon) knownIcons.set(id, previousKnownIcon);
    else knownIcons.delete(id);
    throw err;
  }
}

// The sidebar is mounted app-wide; React Query's shared cache lets a route change reuse the last list
// instead of refetching, and makes an optimistic rename update every mounted instance at once. The
// file-system and workspace duplexers refresh it when Orion creates, renames or otherwise changes a
// workspace.
/**
 * The live icon for a workspace id — read from the same list (and therefore the same merge cache) every
 * badge reads, so an icon edited anywhere repaints every surface at once. `undefined` when the id is
 * unknown or the list hasn't loaded, which is the caller's cue to fall back to a baked icon or the
 * initials tile. Mirrors native `SymphonyStore.workspaceIcon(id)`.
 */
export function useWorkspaceIcon(id: string): string | undefined {
  const state = useWorkspaces();
  if (!id || state.status !== 'ready') return undefined;
  return state.workspaces.find((w) => w.id === id)?.icon;
}

// An agent at work writes files many times a second, and every write arrived as a refetch from every
// mounted useWorkspaces (one per Space badge): one browser re-read the list ~70 times a minute. A burst
// of events now costs ONE read, at most REFETCH_COALESCE_MS after its first event.
const REFETCH_COALESCE_MS = 3_000;
let refetchTimer: ReturnType<typeof setTimeout> | undefined;

export function refetchWorkspacesSoon(): void {
  if (refetchTimer) return;
  refetchTimer = setTimeout(() => {
    refetchTimer = undefined;
    void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces });
  }, REFETCH_COALESCE_MS);
}

export function useWorkspaces(): WorkspacesState {
  const query = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: fetchWorkspaces,
    // The shared QueryClient's retry:false latches this list to `error` on one failed read (a refused
    // gRPC stream, a 401 mid session refresh), and the rail then shows "Spaces" with no rows until a
    // file-system or workspace event happens to refetch it. Retry a blip, and re-read on return to the
    // tab while still errored; a loaded list is left to the duplexers as before.
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: (q) => q.state.status === 'error',
  });

  // Two signals feed the same list: the VFS file-system channel (Orion creating/renaming a workspace
  // via the VFS) and the dedicated workspace domain-events channel the native surfaces already read.
  useFileSystemDuplexer(refetchWorkspacesSoon);
  useWorkspaceDuplexer(refetchWorkspacesSoon);

  // Keep a prior good list on a transient failure; only surface an error before the first success.
  if (query.data) return { status: 'ready', workspaces: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
